import { joinAllArgs } from '@bugbug/core/utils/toolbox';
import { createApi } from '@reduxjs/toolkit/query/react';
import Decimal from 'decimal.js-light';

import type { SubscriptionPlan, SubscriptionPricePreview } from '@bugbug/core/types/subscriptions';
import type { Maybe } from '@bugbug/core/types/utils';
import { axiosBaseQuery } from '~/services/api/baseQuery';

import { formatProductPriceData } from '../organization/organization.helpers';

const TAG = 'subscriptions' as const;
export const subscriptionsApi = createApi({
  reducerPath: 'subscriptionsApi',
  baseQuery: axiosBaseQuery({ baseUrl: '/subscriptions' }),
  tagTypes: [TAG],

  endpoints: (builder) => ({
    // GET /subscriptions/
    getSubscriptionsPlans: builder.query<SubscriptionPlan[], void>({
      query: () => ({
        url: '/',
        method: 'GET',
      }),
      providesTags: () => [{ type: TAG, id: 'LIST' }],
      transformResponse: (response: {
        page: number;
        count: number;
        results: SubscriptionPlan<Maybe<string>>[];
      }) =>
        response.results.map((plan) => {
          const { type: _, ...formattedPlan } =
            formatProductPriceData<SubscriptionPlan<Maybe<string>>>(plan);

          const formattedAddons = formattedPlan.possibleAddons.map((addon) =>
            addon.type === 'extra_parallel_runs_limit' ? formatProductPriceData(addon) : addon,
          );

          const result: SubscriptionPlan = {
            ...formattedPlan,
            type: plan.type,
            possibleAddons: formattedAddons,
          };

          return result;
        }),
    }),

    // POST /subscriptions/preview/
    getSubscriptionPricePreview: builder.query<
      SubscriptionPricePreview,
      {
        price: string;
        addons: { price: string; quantity: number }[];
        countryCode: string;
        coupon?: string;
      }
    >({
      query: ({ price, addons, coupon, countryCode }) => ({
        url: '/preview/',
        method: 'POST',
        data: {
          price,
          addons,
          countryCode: countryCode ?? undefined,
          coupon: coupon ?? null,
        },
      }),
      transformResponse: ({
        currency,
        invalidCoupon,
        ...priceData
      }: SubscriptionPricePreview<Maybe<string>>) => ({
        currency,
        invalidCoupon,
        ...Object.entries(priceData).reduce(
          (acc, [key, value]) => ({
            ...acc,
            [key]: new Decimal(value ?? '0'),
          }),
          {} as Record<keyof typeof priceData, Decimal>,
        ),
      }),
      providesTags: (_, __, args) => [{ type: TAG, id: 'PRICE', args: joinAllArgs(args) }],
    }),

    // POST /subscriptions/cancel/
    cancelDowngrade: builder.mutation<void, void>({
      query: () => ({
        url: '/cancel/',
        method: 'POST',
      }),
      invalidatesTags: [{ type: TAG }],
    }),
  }),
});

export const {
  useGetSubscriptionsPlansQuery,
  useGetSubscriptionPricePreviewQuery,
  useCancelDowngradeMutation,
} = subscriptionsApi;
